import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { 
  ChartBarIcon, 
  DocumentTextIcon, 
  ExclamationTriangleIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { reportAPI, formatCurrency, formatDate } from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const Reports = () => {
  const [selectedReport, setSelectedReport] = useState('business-summary');
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  });

  const [defaulterMonths, setDefaulterMonths] = useState(3);

  const { data, isLoading, error, refetch } = useQuery(
    [selectedReport, dateRange, defaulterMonths],
    () => {
      // Ensure dates are properly formatted
      const params = {
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      };
      
      switch (selectedReport) {
        case 'business-summary':
          return reportAPI.getBusinessSummary(params);
        case 'outstanding-loans':
          return reportAPI.getOutstandingLoans({ limit: 50 });
        case 'defaulter-list':
          return reportAPI.getDefaulterList({ limit: 50, threshold_months: defaulterMonths });
        case 'interest-report':
          return reportAPI.getInterestReport(params);
        case 'item-analysis':
          return reportAPI.getItemAnalysis(params);
        default:
          return reportAPI.getBusinessSummary(params);
      }
    },
    {
      keepPreviousData: true,
    }
  );

  const reportTypes = [
    {
      id: 'business-summary',
      name: 'Business Summary',
      description: 'Overview of business performance and key metrics',
      icon: ChartBarIcon,
      color: 'blue',
    },
    {
      id: 'outstanding-loans',
      name: 'Outstanding Loans',
      description: 'List of all active loans with outstanding amounts',
      icon: DocumentTextIcon,
      color: 'green',
    },
    {
      id: 'defaulter-list',
      name: 'Defaulter List',
      description: 'Customers with overdue payments',
      icon: ExclamationTriangleIcon,
      color: 'red',
    },
    {
      id: 'interest-report',
      name: 'Interest Report',
      description: 'Detailed interest calculations and accruals',
      icon: ClockIcon,
      color: 'yellow',
    },
    {
      id: 'item-analysis',
      name: 'Item Analysis',
      description: 'Analysis of pledged items by category and purity',
      icon: CurrencyDollarIcon,
      color: 'purple',
    },
  ];

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };



  const renderReportContent = () => {
    if (isLoading) {
      return <LoadingSpinner size="lg" />;
    }

    if (error) {
      console.error('Report error:', error);
      return (
        <div className="text-center py-12">
          <p className="text-red-600">Error loading report: {error.message}</p>
          {error.response?.data?.message && (
            <p className="text-red-500 text-sm mt-2">{error.response.data.message}</p>
          )}
        </div>
      );
    }

    if (!data) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">No data available</p>
        </div>
      );
    }

    switch (selectedReport) {
      case 'business-summary':
        return <BusinessSummaryReport data={data.data} />;
      case 'outstanding-loans':
        return <OutstandingLoansReport data={data.data} />;
      case 'defaulter-list':
        return <DefaulterListReport data={data.data} />;
      case 'interest-report':
        return <InterestReport data={data.data} onRefresh={refetch} />;
      case 'item-analysis':
        return <ItemAnalysisReport data={data.data} />;
      default:
        return <BusinessSummaryReport data={data.data} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-primary-700">Reports</h1>
        <p className="mt-1 text-sm text-gray-500">
          Business analytics and reporting
        </p>
      </div>

      {/* Report type selection */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {reportTypes.map((report) => {
          const Icon = report.icon;
          return (
            <button
              key={report.id}
              onClick={() => setSelectedReport(report.id)}
              className={`relative rounded-lg border p-6 text-left hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                selectedReport === report.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-center">
                <div className={`flex-shrink-0 h-8 w-8 rounded-md bg-${report.color}-100 flex items-center justify-center`}>
                  <Icon className={`h-5 w-5 text-${report.color}-600`} />
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-900">{report.name}</h3>
                  <p className="text-sm text-gray-500">{report.description}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Date range filter */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Date Range Filter</h3>
          <p className="text-sm text-gray-600">
            Current selection: {formatDate(dateRange.start_date)} to {formatDate(dateRange.end_date)}
          </p>
          {isLoading && (
            <p className="text-sm text-blue-600 mt-1">
              <span className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-2"></span>
              Loading data for selected date range...
            </p>
          )}
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="flex-1 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
                Start Date
              </label>
              <input
                type="date"
                id="start_date"
                value={dateRange.start_date}
                onChange={(e) => handleDateRangeChange('start_date', e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
                End Date
              </label>
              <input
                type="date"
                id="end_date"
                value={dateRange.end_date}
                onChange={(e) => handleDateRangeChange('end_date', e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const today = new Date();
                const startOfYear = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
                const endDate = today.toISOString().split('T')[0];
                setDateRange({ start_date: startOfYear, end_date: endDate });
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              This Year
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
                const endDate = today.toISOString().split('T')[0];
                setDateRange({ start_date: startOfMonth, end_date: endDate });
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              This Month
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
                setDateRange({ 
                  start_date: lastMonth.toISOString().split('T')[0], 
                  end_date: endOfLastMonth.toISOString().split('T')[0] 
                });
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Last Month
            </button>
          </div>
        </div>
      </div>

      {/* Report content */}
      <div className="bg-white shadow rounded-lg">
        {selectedReport === 'defaulter-list' && (
          <div className="p-4 border-b border-gray-200 flex items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Months without payment</label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="number"
                  min={1}
                  max={24}
                  value={defaulterMonths}
                  onChange={(e)=> setDefaulterMonths(Math.max(1, Math.min(24, parseInt(e.target.value||'1'))))}
                  className="mt-0 block w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                <button
                  onClick={()=>refetch()}
                  className="px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >Apply</button>
              </div>
            </div>
          </div>
        )}
        {renderReportContent()}
      </div>
    </div>
  );
};

// Report components
const BusinessSummaryReport = ({ data }) => {
  // Handle the case where we receive the full API response instead of just the data
  const actualData = data?.data || data;
  const { period, loans, payments, customers } = actualData || {};
  const monthly_trend = Array.isArray(actualData?.monthly_trend) ? actualData.monthly_trend : [];
  const top_customers = Array.isArray(actualData?.top_customers) ? actualData.top_customers : [];
  
  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Business Summary</h3>
        <p className="text-sm text-gray-600">
          Period: {period?.start_date ? formatDate(period.start_date) : 'N/A'} to {period?.end_date ? formatDate(period.end_date) : 'N/A'}
        </p>
        
        {(loans?.total_loans || 0) === 0 && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-sm text-blue-800">
              <strong>No data found</strong> for the selected date range. 
              Try adjusting your date range to see data from different periods.
            </p>
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-blue-800">Total Loans</h4>
          <p className="text-2xl font-bold text-blue-900">{Number(loans?.total_loans) || 0}</p>
          <p className="text-xs text-blue-600 mt-1">
            Active: {Number(loans?.active_loans) || 0} | Closed: {Number(loans?.closed_loans) || 0}
          </p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-green-800">Active Loans</h4>
          <p className="text-2xl font-bold text-green-900">{Number(loans?.active_loans) || 0}</p>
          <p className="text-xs text-green-600 mt-1">
            Avg Amount: {formatCurrency(Number(loans?.avg_loan_amount) || 0)}
          </p>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-yellow-800">Total Disbursed</h4>
          <p className="text-2xl font-bold text-yellow-900">
            {formatCurrency(Number(loans?.total_loan_amount) || 0)}
          </p>
          <p className="text-xs text-yellow-600 mt-1">
            Processing Charges: {formatCurrency(Number(loans?.total_processing_charges) || 0)}
          </p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-purple-800">Total Collections</h4>
          <p className="text-2xl font-bold text-purple-900">
            {formatCurrency(Number(payments?.total_payments_amount) || 0)}
          </p>
          <p className="text-xs text-purple-600 mt-1">
            Principal: {formatCurrency(Number(payments?.total_principal_collected) || 0)} | 
            Interest: {formatCurrency(Number(payments?.total_interest_collected) || 0)}
          </p>
        </div>
      </div>

      {/* Additional insights */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-md font-medium text-gray-900 mb-3">Customer Statistics</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total Customers</span>
              <span className="text-sm font-medium">{Number(customers?.total_customers) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Active Customers</span>
              <span className="text-sm font-medium">{Number(customers?.active_customers) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Customers with Loans</span>
              <span className="text-sm font-medium">{Number(customers?.customers_with_loans) || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-md font-medium text-gray-900 mb-3">Payment Statistics</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total Payments</span>
              <span className="text-sm font-medium">{Number(payments?.total_payments) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Avg Payment Amount</span>
              <span className="text-sm font-medium">{formatCurrency(Number(payments?.avg_payment_amount) || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Principal Collected</span>
              <span className="text-sm font-medium">{formatCurrency(Number(payments?.total_principal_collected) || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Interest Collected</span>
              <span className="text-sm font-medium">{formatCurrency(Number(payments?.total_interest_collected) || 0)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly trend */}
      {monthly_trend && monthly_trend.length > 0 && (
        <div className="mt-6 bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-md font-medium text-gray-900 mb-3">Monthly Disbursement Trend</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-primary-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Loans</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {monthly_trend.map((month, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 text-sm text-gray-900">{month.month}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{Number(month.loan_count)}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(Number(month.disbursed_amount))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top customers */}
      {top_customers && top_customers.length > 0 && (
        <div className="mt-6 bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-md font-medium text-gray-900 mb-3">Top Customers by Loan Amount</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-primary-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Loans</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {top_customers.map((customer, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 text-sm text-gray-900">
                      {customer.first_name} {customer.last_name}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900">{customer.mobile_1}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{Number(customer.loan_count)}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(Number(customer.total_loan_amount))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const OutstandingLoansReport = ({ data }) => {
  // Ensure we always have a valid array for loans
  const actualData = data?.data || data;
  const loans = Array.isArray(actualData?.loans) ? actualData.loans : [];
  const pagination = actualData?.pagination;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Outstanding Loans</h3>
        <p className="text-sm text-gray-600">
          Active loans that are currently outstanding
        </p>
      </div>

      {loans.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No outstanding loans found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-primary-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Loan Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Loan Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Principal Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Outstanding Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interest Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loans.map((loan) => (
                <tr key={loan.id} className="hover:bg-primary-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {loan.first_name} {loan.last_name}
                    </div>
                    <div className="text-sm text-gray-500">{loan.customer_code}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {loan.loan_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(loan.loan_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(Number(loan.loan_amount))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(Number(loan.outstanding_amount))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {Number(loan.interest_rate)}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      loan.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {loan.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {pagination.start} to {pagination.end} of {pagination.total} results
          </div>
          <div className="flex space-x-2">
            {pagination.page > 1 && (
              <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-primary-50">
                Previous
              </button>
            )}
            {pagination.page < pagination.totalPages && (
              <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-primary-50">
                Next
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DefaulterListReport = ({ data }) => {
  // Ensure we always have a valid array for defaulters
  const actualData = data?.data || data;
  const defaulters = Array.isArray(actualData?.defaulters) ? actualData.defaulters : [];
  const pagination = actualData?.pagination;
  const debugInfo = actualData?.debug_info;

  // Contact tracking (localStorage per day) similar to InterestReport
  const todayKey = new Date().toISOString().slice(0, 10);
  const [contactStatus, setContactStatus] = useState(() => {
    try {
      const raw = localStorage.getItem('defaulterContactStatus');
      return raw ? JSON.parse(raw) : {};
    } catch (_) {
      return {};
    }
  });

  const markContactStatus = (key, type) => {
    setContactStatus((prev) => {
      const next = { ...prev, [key]: { ...(prev[key] || {}) } };
      if (type === 'call') next[key].called = todayKey;
      if (type === 'whatsapp') next[key].whatsapp = todayKey;
      try { localStorage.setItem('defaulterContactStatus', JSON.stringify(next)); } catch (_) {}
      return next;
    });
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Defaulter List</h3>
        <p className="text-sm text-gray-600">
          Customers with loans that have no payments for {actualData?.threshold_months || 3}+ months (includes active and defaulted loans)
        </p>
        
       
      </div>

      {defaulters.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No defaulters found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-primary-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Loan Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Loan Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Outstanding Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Payment Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Days Since Last Payment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pending Months
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {defaulters.map((defaulter) => {
                const phone = defaulter.mobile_1 || defaulter.mobile || '';
                const name = `${defaulter.first_name || ''} ${defaulter.last_name || ''}`.trim();
                const loanCode = defaulter.loan_number || '';
                const statusKey = `${loanCode}|${phone}`;
                const status = contactStatus[statusKey] || {};
                const calledToday = status.called === todayKey;
                const whatsappToday = status.whatsapp === todayKey;
                const callHref = phone ? `tel:${phone}` : '#';
                const whatsappHref = phone ? `https://wa.me/${phone}?text=${encodeURIComponent('Dear ' + (name || 'Customer') + ', this is a reminder to pay your pending loan interest.')}` : '#';
                return (
                <tr key={defaulter.id} className="hover:bg-primary-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {defaulter.first_name} {defaulter.last_name}
                    </div>
                    <div className="text-sm text-gray-500">{defaulter.customer_code}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {defaulter.loan_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(defaulter.loan_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(Number(defaulter.outstanding_amount))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {defaulter.last_payment_date ? formatDate(defaulter.last_payment_date) : 'No payments'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                    {defaulter.days_since_last_payment} days
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {Number.isFinite(Number(defaulter.pending_months)) ? Number(defaulter.pending_months) : Math.floor(Number(defaulter.days_since_last_payment || 0)/30)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center space-x-2">
                      <a
                        href={callHref}
                        onClick={()=> phone && markContactStatus(statusKey,'call')}
                        className={`px-2 py-1 border rounded text-xs ${phone ? 'text-blue-700 border-blue-300 hover:bg-blue-50' : 'text-gray-400 border-gray-200 pointer-events-none'}`}
                      >Call</a>
                      <a
                        href={whatsappHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={()=> phone && markContactStatus(statusKey,'whatsapp')}
                        className={`px-2 py-1 border rounded text-xs ${phone ? 'text-emerald-700 border-emerald-300 hover:bg-emerald-50' : 'text-gray-400 border-gray-200 pointer-events-none'}`}
                      >WhatsApp</a>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center space-x-2">
                      {calledToday && <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">Called Today</span>}
                      {whatsappToday && <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-800">WhatsApp Today</span>}
                      {!calledToday && !whatsappToday && <span className="text-gray-400">—</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                      Defaulter
                    </span>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
      )}

      {pagination && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {pagination.start} to {pagination.end} of {pagination.total} results
          </div>
          <div className="flex space-x-2">
            {pagination.page > 1 && (
              <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-primary-50">
                Previous
              </button>
            )}
            {pagination.page < pagination.totalPages && (
              <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-primary-50">
                Next
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const InterestReport = ({ data, onRefresh }) => {
  const actualData = data?.data || data;
  const { summary } = actualData || {};
  // Support both old and new keys from API
  const calculations = Array.isArray(actualData?.calculations)
    ? actualData.calculations
    : (Array.isArray(actualData?.interest_calculations) ? actualData.interest_calculations : []);
  const [calcPage, setCalcPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(1);
  const [phoneSelections, setPhoneSelections] = useState({});
  // Store per-row contact status keyed by a stable key. Persist per day in localStorage
  const [contactStatus, setContactStatus] = useState(() => {
    try {
      const raw = localStorage.getItem('pendingContactStatus');
      return raw ? JSON.parse(raw) : {};
    } catch (_) {
      return {};
    }
  });

  const todayKey = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const markContactStatus = (key, type) => {
    setContactStatus((prev) => {
      const next = { ...prev, [key]: { ...(prev[key] || {}) } };
      if (type === 'call') next[key].called = todayKey;
      if (type === 'whatsapp') next[key].whatsapp = todayKey;
      try { localStorage.setItem('pendingContactStatus', JSON.stringify(next)); } catch (_) {}
      return next;
    });
  };
  const calcPageSize = 5;
  const pendingPageSize = 5;
  // Pending interest list coming from API: prefer actualData.pending or actualData.pending_interest
  let pendingList = Array.isArray(actualData?.pending)
    ? actualData.pending
    : (Array.isArray(actualData?.pending_interest) ? actualData.pending_interest : []);

  // If API doesn't provide a pending list, derive a simple one from calculations
  if (!Array.isArray(pendingList) || pendingList.length === 0) {
    pendingList = calculations.map((c) => ({
      first_name: c.first_name,
      last_name: c.last_name,
      father_name: c.father_name,
      guardian_name: c.guardian_name,
      email: c.email,
      mobile_1: c.mobile_1,
      loan_number: c.loan_number,
      month: String(c.calculation_date).slice(0, 7),
      pending_interest: c.interest_amount
    }));
  }

  // Paged data builders
  const calcTotalPages = Math.max(1, Math.ceil((calculations?.length || 0) / calcPageSize));
  const calcPageData = useMemo(() => {
    const start = (calcPage - 1) * calcPageSize;
    return (calculations || []).slice(start, start + calcPageSize);
  }, [calculations, calcPage]);

  const pendingTotalPages = Math.max(1, Math.ceil((pendingList?.length || 0) / pendingPageSize));
  const pendingPageData = useMemo(() => {
    const start = (pendingPage - 1) * pendingPageSize;
    return (pendingList || []).slice(start, start + pendingPageSize);
  }, [pendingList, pendingPage]);

  const handleCalculateInterest = async () => {
    try {
      const response = await reportAPI.calculateAllInterest();
      if (response.data.success) {
        alert(`Interest calculation completed successfully!\n\n${response.data.message}`);
        if (onRefresh) onRefresh();
      } else {
        alert('Failed to calculate interest: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error calculating interest:', error);
      alert('Error calculating interest: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Interest Report</h3>
            <p className="text-sm text-gray-600">
              Interest calculations and summaries for loans
            </p>
          </div>
          <button
            onClick={handleCalculateInterest}
            className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Calculate All Interest
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 mb-8">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-blue-800">Total Calculations</h4>
          <p className="text-2xl font-bold text-blue-900">
            {Number(summary?.total_calculations) || 0}
          </p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-green-800">Total Interest</h4>
          <p className="text-2xl font-bold text-green-900">
            {formatCurrency(Number(summary?.total_interest_calculated) || 0)}
          </p>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-yellow-800">Average Monthly</h4>
          <p className="text-2xl font-bold text-yellow-900">
            {formatCurrency(Number(summary?.average_monthly_interest) || 0)}
          </p>
        </div>
      </div>

      {/* Detailed Calculations Table */}
      {calculations.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-primary-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Loan Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Father/Guardian
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Calculation Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Principal Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interest Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interest Amount
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {calcPageData.map((calc) => (
                <tr key={calc.id} className="hover:bg-primary-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {calc.loan_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {calc.first_name} {calc.last_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {calc.father_name || calc.guardian_name || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(calc.calculation_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(Number((calc.principal_amount != null ? calc.principal_amount : (calc.loan_amount != null ? calc.loan_amount : 0))))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {Number(calc.interest_rate)}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(Number(calc.interest_amount))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between p-2 text-sm">
            <div>Page {calcPage} of {calcTotalPages}</div>
            <div className="space-x-2">
              <button className="px-2 py-1 border rounded disabled:opacity-50" onClick={()=>setCalcPage((p)=>Math.max(1,p-1))} disabled={calcPage<=1}>Prev</button>
              <button className="px-2 py-1 border rounded disabled:opacity-50" onClick={()=>setCalcPage((p)=>Math.min(calcTotalPages,p+1))} disabled={calcPage>=calcTotalPages}>Next</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-500">No interest calculations found for the selected period</p>
        </div>
      )}

      {/* Pending Interest with Contact Actions */}
      <div className="mt-8">
        <h4 className="text-md font-medium text-gray-900 mb-3">Pending Interest Contacts</h4>
        {pendingList.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-primary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Father/Guardian</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Loan Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pending Interest</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pendingPageData.map((row, idx) => {
                  const email = row.email || row.customer_email || '';
                  const phonesRaw = [row.mobile_1, row.mobile_2, row.phone, row.mobile].filter(Boolean);
                  const phones = Array.from(new Set(phonesRaw));
                  const selectedPhone = phoneSelections[idx] || phones[0] || '';
                  const month = row.month || row.month_key || row.calculation_month || '';
                  const interest = Number(row.pending_interest || row.interest_amount || 0);
                  const name = `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.customer_name || 'Unknown';
                  const loanCode = row.loan_number || row.loan_code || '';
                  const whatsappHref = selectedPhone ? `https://wa.me/${selectedPhone}?text=${encodeURIComponent('Dear ' + name + ', this is a reminder for your pending monthly interest.')}` : '#';
                  const smsHref = selectedPhone ? `sms:${selectedPhone}?body=${encodeURIComponent('Reminder: Your monthly interest is pending.')}` : '#';
                  const callHref = selectedPhone ? `tel:${selectedPhone}` : '#';
                  const mailHref = email ? `mailto:${email}?subject=${encodeURIComponent('Pending Interest Reminder')}&body=${encodeURIComponent('Dear ' + name + ',%0D%0A%0D%0AThis is a reminder for your pending monthly interest. Please make the payment at the earliest.%0D%0A%0D%0AThank you.')}` : '#';
                  const statusKey = `${loanCode || 'loan'}|${month || ''}|${selectedPhone || ''}`;
                  const status = contactStatus[statusKey] || {};
                  const calledToday = status.called === todayKey;
                  const whatsappToday = status.whatsapp === todayKey;
                  return (
                    <tr key={idx} className="hover:bg-primary-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.father_name || row.guardian_name || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center space-x-2">
                          {phones.length > 1 ? (
                            <select className="input h-8 text-sm" value={selectedPhone} onChange={(e)=> setPhoneSelections(prev=>({ ...prev, [idx]: e.target.value }))}>
                              {phones.map((p)=> <option key={p} value={p}>{p}</option>)}
                            </select>
                          ) : (
                            <span>{selectedPhone || '—'}</span>
                          )}
                        </div>
                        <div className="text-gray-500 text-xs">{email || '—'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{loanCode || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{month || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(interest)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center space-x-2">
                          {calledToday && <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">Called Today</span>}
                          {whatsappToday && <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-800">WhatsApp Today</span>}
                          {!calledToday && !whatsappToday && <span className="text-gray-400">—</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center space-x-2">
                          <a href={callHref} onClick={()=>markContactStatus(statusKey,'call')} className={`px-2 py-1 border rounded text-xs ${selectedPhone ? 'text-blue-700 border-blue-300 hover:bg-blue-50' : 'text-gray-400 border-gray-200 pointer-events-none'}`}>Call</a>
                          {/* <a href={smsHref} className={`px-2 py-1 border rounded text-xs ${selectedPhone ? 'text-green-700 border-green-300 hover:bg-green-50' : 'text-gray-400 border-gray-200 pointer-events-none'}`}>SMS</a> */}
                          <a href={whatsappHref} target="_blank" rel="noopener noreferrer" onClick={()=>markContactStatus(statusKey,'whatsapp')} className={`px-2 py-1 border rounded text-xs ${selectedPhone ? 'text-emerald-700 border-emerald-300 hover:bg-emerald-50' : 'text-gray-400 border-gray-200 pointer-events-none'}`}>WhatsApp</a>
                          {/* <a href={mailHref} className={`px-2 py-1 border rounded text-xs ${email ? 'text-purple-700 border-purple-300 hover:bg-purple-50' : 'text-gray-400 border-gray-200 pointer-events-none'}`}>Email</a> */}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex items-center justify-between p-2 text-sm">
              <div>Page {pendingPage} of {pendingTotalPages}</div>
              <div className="space-x-2">
                <button className="px-2 py-1 border rounded disabled:opacity-50" onClick={()=>setPendingPage((p)=>Math.max(1,p-1))} disabled={pendingPage<=1}>Prev</button>
                <button className="px-2 py-1 border rounded disabled:opacity-50" onClick={()=>setPendingPage((p)=>Math.min(pendingTotalPages,p+1))} disabled={pendingPage>=pendingTotalPages}>Next</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">No pending interest found</div>
        )}
      </div>
    </div>
  );
};

const ItemAnalysisReport = ({ data }) => {
  const actualData = data?.data || data;
  const { summary } = actualData || {};
  const category_analysis = Array.isArray(actualData?.category_analysis) ? actualData.category_analysis : [];
  const purity_analysis = Array.isArray(actualData?.purity_analysis) ? actualData.purity_analysis : [];
  const items = Array.isArray(actualData?.items) ? actualData.items : [];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Item Analysis</h3>
        <p className="text-sm text-gray-600">
          Analysis of pledged items by category and purity
        </p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 mb-8">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-blue-800">Total Items</h4>
            <p className="text-2xl font-bold text-blue-900">
              {Number(summary.total_items) || 0}
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-green-800">Total Weight</h4>
            <p className="text-2xl font-bold text-green-900">
              {Number(summary.total_weight) || 0} grams
            </p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-yellow-800">Total Value</h4>
            <p className="text-2xl font-bold text-yellow-900">
              {formatCurrency(Number(summary.total_value) || 0)}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Category Analysis */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-md font-medium text-gray-900 mb-3">By Category</h4>
          {category_analysis.length > 0 ? (
            <div className="space-y-2">
              {category_analysis.map((category) => (
                <div key={category.category_name} className="flex justify-between items-center p-2 bg-primary-50 rounded">
                  <span className="text-sm font-medium">{category.category_name}</span>
                  <span className="text-sm text-gray-600">
                    {Number(category.item_count)} items - {formatCurrency(Number(category.total_value))}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No category data available</p>
          )}
        </div>

        {/* Purity Analysis */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-md font-medium text-gray-900 mb-3">By Purity</h4>
          {purity_analysis.length > 0 ? (
            <div className="space-y-2">
              {purity_analysis.map((purity) => (
                <div key={purity.purity} className="flex justify-between items-center p-2 bg-primary-50 rounded">
                  <span className="text-sm font-medium">{purity.purity}</span>
                  <span className="text-sm text-gray-600">
                    {Number(purity.item_count)} items - {formatCurrency(Number(purity.total_value))}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No purity data available</p>
          )}
        </div>
      </div>

      {/* Detailed Items Table */}
      {actualData?.items && actualData.items.length > 0 && (
        <div className="mt-8">
          <h4 className="text-md font-medium text-gray-900 mb-3">Item Details</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-primary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Purity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Weight (grams)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Loan Code
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-primary-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.category_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.purity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {Number(item.weight)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(Number(item.value))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.loan_number}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
